import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Phone, Shield, Hash, PhoneCall, UserPlus, Zap,
  Check, ArrowRight, Loader2, ChevronRight, Copy,
  MessageSquare, Clock, CreditCard, Crown, Star,
  Mic, Globe, Type, Calendar as CalendarIcon, Sparkles,
  Volume2, Rocket, CheckCircle2, AlertCircle, ExternalLink,
} from 'lucide-react';
import { IndustryPreviewCard } from '../components/IndustryBrain';

const API = process.env.REACT_APP_BACKEND_URL;
const TOTAL_SCREENS = 5;

const LANG_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'zh', label: 'Chinese' },
  { code: 'ko', label: 'Korean' },
  { code: 'vi', label: 'Vietnamese' },
];

export default function OnboardingPage() {
  const { token, user, fetchMe } = useAuth();
  const { name: brandName } = useBrand();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const h = { Authorization: `Bearer ${token}` };

  const [screen, setScreen] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [industryProfile, setIndustryProfile] = useState(null);

  // Screen 1: Phone + Number
  const [phone, setPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [previewCode, setPreviewCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [areaCode, setAreaCode] = useState('');
  const [numbersMessage, setNumbersMessage] = useState('');
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyNameSaved, setCompanyNameSaved] = useState(false);

  // Screen 2: Voice + Language + Greeting
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('standard');
  const [defaultLang, setDefaultLang] = useState('en');
  const [multilingualEnabled, setMultilingualEnabled] = useState(false);
  const [extraLangs, setExtraLangs] = useState([]);
  const [greetingTemplates, setGreetingTemplates] = useState([]);
  const [greetingText, setGreetingText] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('general');

  // Screen 3: Calendar + Lead
  const [availability, setAvailability] = useState([
    { day: 'monday', start: '09:00', end: '17:00', enabled: true },
    { day: 'tuesday', start: '09:00', end: '17:00', enabled: true },
    { day: 'wednesday', start: '09:00', end: '17:00', enabled: true },
    { day: 'thursday', start: '09:00', end: '17:00', enabled: true },
    { day: 'friday', start: '09:00', end: '17:00', enabled: true },
    { day: 'saturday', start: '10:00', end: '15:00', enabled: true },
    { day: 'sunday', start: '00:00', end: '00:00', enabled: false },
  ]);
  const [showingDuration, setShowingDuration] = useState(30);
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadType, setLeadType] = useState('buyer');

  // Screen 4: Plan Selection + Payment
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('trial');
  const [selectedPlanCode, setSelectedPlanCode] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [confirmingPayment, setConfirmingPayment] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding/status`, { headers: h });
      setStatus(data);
      if (data.onboarding_complete) { await fetchMe(token); navigate('/dashboard'); return; }
      if (data.phone_verified) setPhoneVerified(true);
      if (data.brokerage_name) {
        setCompanyName(data.brokerage_name);
        setCompanyNameSaved(true);
      }
      if (data.ai_number_display || data.ai_number) {
        setSelectedNumber({ display: data.ai_number_display, raw: data.ai_number });
      }
      if (data.voice_selected) setSelectedVoice(data.voice_selected);
      if (data.default_language) setDefaultLang(data.default_language);
      if (data.multilingual_enabled) setMultilingualEnabled(true);
      if (data.plan_type) setSelectedPlan(data.plan_type);
      if (data.plan_code) setSelectedPlanCode(data.plan_code);
      if (data.payment_confirmed) setPaymentConfirmed(true);
      // Auto-advance if phone + number already done
      if (data.phone_verified && data.ai_number) setScreen(2);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Load industry profile for onboarding preview
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/industry/me`, { headers: h }).then(res => {
      if (res.data?.full_profile) setIndustryProfile(res.data.full_profile);
    }).catch(() => {});
  }, [token]);

  // Handle return from FTD payment page (?setup_complete=true or ?step=payment-complete)
  useEffect(() => {
    const setupComplete = searchParams.get('setup_complete');
    const stepParam = searchParams.get('step');
    if ((setupComplete === 'true' || stepParam === 'payment-complete') && token && !paymentConfirmed) {
      setScreen(4);
      setConfirmingPayment(true);
      setSelectedPlan('paid');
      let retries = 0;
      const maxRetries = 3;

      const checkPayment = async () => {
        try {
          const { data } = await axios.post(`${API}/api/onboarding/confirm-payment`, {}, { headers: { Authorization: `Bearer ${token}` } });
          if (data.confirmed) {
            setPaymentConfirmed(true);
            setPaymentError(null);
            setConfirmingPayment(false);
          } else if (retries < maxRetries) {
            // Webhook may not have arrived yet — retry after delay
            retries++;
            setTimeout(checkPayment, 2000);
          } else {
            setPaymentError(data.message || 'Payment verification timed out. Please refresh the page.');
            setConfirmingPayment(false);
          }
        } catch (err) {
          setPaymentError(err.response?.data?.detail || 'Failed to verify payment.');
          setConfirmingPayment(false);
        }
      };

      checkPayment();

      // Clean URL params
      searchParams.delete('setup_complete');
      searchParams.delete('step');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, token, paymentConfirmed]);

  // Fetch voice options + greeting templates when reaching screen 2
  useEffect(() => {
    if (screen === 2) {
      axios.get(`${API}/api/onboarding/voice-options`, { headers: h })
        .then(r => setVoiceOptions(r.data.voices || [])).catch(() => {});
      axios.get(`${API}/api/onboarding/greeting-templates`, { headers: h })
        .then(r => {
          const t = r.data.templates || [];
          setGreetingTemplates(t);
          const gen = t.find(x => x.type === 'general');
          if (gen && !greetingText) setGreetingText(gen.text);
        }).catch(() => {});
    }
  }, [screen]);

  // Fetch plans when reaching screen 4
  useEffect(() => {
    if (screen === 4 && plans.length === 0) {
      axios.get(`${API}/api/billing/plans`)
        .then(r => setPlans(r.data.plans || []))
        .catch(() => {});
    }
  }, [screen]);

  // ── Save company name ──
  const saveCompanyName = async () => {
    if (!companyName.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/set-company-name`, { company_name: companyName.trim() }, { headers: h });
      setCompanyNameSaved(true);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // ── Screen 1 handlers ──
  const sendOtp = async () => {
    if (!phone.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/send-otp`, { phone }, { headers: h });
      setOtpSent(true);
      if (data.preview_code) setPreviewCode(data.preview_code);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to send code'); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/verify-otp`, { phone, code: otp }, { headers: h });
      setPhoneVerified(true);
      fetchNumbers();
    } catch (err) { alert(err.response?.data?.detail || 'Invalid code'); }
    finally { setLoading(false); }
  };

  const fetchNumbers = useCallback(async (ac) => {
    setSearchingNumbers(true);
    try {
      const params = ac ? `?area_code=${encodeURIComponent(ac)}` : '';
      const { data } = await axios.get(`${API}/api/onboarding/available-numbers${params}`, { headers: h });
      setNumbers(data.numbers || []);
      setNumbersMessage(data.message || '');
    } catch { /* ignore */ }
    setSearchingNumbers(false);
  }, [token]);

  const pickNumber = async (num) => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/pick-number`, { number: num.raw }, { headers: h });
      setSelectedNumber({ display: data.display, raw: data.number });
      setScreen(2);
    } catch (err) { alert(err.response?.data?.detail || 'Failed to assign number'); }
    finally { setLoading(false); }
  };

  // ── Quick Setup (skip to Screen 4 with defaults) ──
  const quickSetup = async () => {
    setLoading(true);
    try {
      // Fetch greeting template for default text
      let defaultGreeting = `Hello! I'm Ava, your AI real estate assistant. How can I help you today?`;
      try {
        const { data: tpl } = await axios.get(`${API}/api/onboarding/greeting-templates`, { headers: h });
        const gen = (tpl.templates || []).find(t => t.type === 'general');
        if (gen) defaultGreeting = gen.text;
      } catch { /* use fallback */ }

      // Save all defaults in parallel
      const defaultBlocks = [
        { day: 'monday', start: '09:00', end: '17:00' },
        { day: 'tuesday', start: '09:00', end: '17:00' },
        { day: 'wednesday', start: '09:00', end: '17:00' },
        { day: 'thursday', start: '09:00', end: '17:00' },
        { day: 'friday', start: '09:00', end: '17:00' },
        { day: 'saturday', start: '10:00', end: '15:00' },
      ];
      await Promise.all([
        axios.post(`${API}/api/onboarding/select-voice`, { voice_tier: 'standard' }, { headers: h }),
        axios.post(`${API}/api/onboarding/setup-language`, { default_language: 'en', multilingual_enabled: false, additional_languages: [] }, { headers: h }),
        axios.post(`${API}/api/onboarding/setup-greeting`, { greeting_text: defaultGreeting, template_type: 'general' }, { headers: h }),
        axios.post(`${API}/api/onboarding/setup-calendar`, { availability_blocks: defaultBlocks, default_showing_duration: 30 }, { headers: h }),
      ]);

      // Update local state for summary screen
      setSelectedVoice('standard');
      setDefaultLang('en');
      setMultilingualEnabled(false);
      setGreetingText(defaultGreeting);
      setSelectedTemplate('general');
      setAvailability(prev => prev.map(a => a.day === 'sunday' ? a : { ...a, enabled: true, start: a.day === 'saturday' ? '10:00' : '09:00', end: a.day === 'saturday' ? '15:00' : '17:00' }));
      setShowingDuration(30);

      setScreen(4);  // Go to Plan Selection
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // ── Screen 2 handler ──
  const savePersonality = async () => {
    setLoading(true);
    try {
      await Promise.all([
        axios.post(`${API}/api/onboarding/select-voice`, { voice_tier: selectedVoice }, { headers: h }),
        axios.post(`${API}/api/onboarding/setup-language`, { default_language: defaultLang, multilingual_enabled: multilingualEnabled, additional_languages: extraLangs }, { headers: h }),
        axios.post(`${API}/api/onboarding/setup-greeting`, { greeting_text: greetingText, template_type: selectedTemplate }, { headers: h }),
      ]);
      setScreen(3);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // ── Screen 3 handler ──
  const saveCalendarAndLead = async () => {
    setLoading(true);
    try {
      const blocks = availability.filter(a => a.enabled).map(a => ({ day: a.day, start: a.start, end: a.end }));
      const promises = [
        axios.post(`${API}/api/onboarding/setup-calendar`, { availability_blocks: blocks, default_showing_duration: showingDuration }, { headers: h }),
      ];
      if (leadName.trim() && leadPhone.trim()) {
        promises.push(
          axios.post(`${API}/api/onboarding/add-lead`, { full_name: leadName, phone: leadPhone, email: leadEmail, lead_type: leadType }, { headers: h })
        );
      }
      await Promise.all(promises);
      setScreen(4);  // Go to Plan Selection
    } catch (err) { alert(err.response?.data?.detail || 'Something went wrong'); }
    finally { setLoading(false); }
  };

  // ── Screen 4 handler: Plan Selection ──
  const selectPlan = async () => {
    setLoading(true);
    setPaymentError(null);
    try {
      if (selectedPlan === 'trial') {
        await axios.post(`${API}/api/onboarding/select-plan`, { plan_type: 'trial' }, { headers: h });
        setPaymentConfirmed(true);
        setScreen(5);
      } else {
        if (!selectedPlanCode) {
          setPaymentError('Please select a plan.');
          setLoading(false);
          return;
        }
        const { data } = await axios.post(`${API}/api/onboarding/select-plan`, {
          plan_type: 'paid',
          plan_code: selectedPlanCode,
        }, { headers: h });

        if (data.hosted_payment_url) {
          setPaymentUrl(data.hosted_payment_url);
          // Redirect to 123cents hosted page
          window.location.href = data.hosted_payment_url;
        } else {
          setPaymentError('Failed to get payment URL. Please try again.');
        }
      }
    } catch (err) {
      setPaymentError(err.response?.data?.detail || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  // ── Screen 5 handler: Activate ──
  const activate = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/onboarding/complete`, {}, { headers: h });
      if (data && data.phone_ready === false) toast.warning(data.phone_message, { duration: 10000 });
      await fetchMe(token);
      navigate('/onboarding/success');
    } catch { navigate('/dashboard'); }
  };

  const voiceIcons = { standard: <Volume2 size={18} className="text-blue-400" />, human_like: <Mic size={18} className="text-amber-400" />, premium: <Sparkles size={18} className="text-emerald-400" /> };
  const voiceColors = { standard: 'border-blue-500/20 bg-blue-500/5', human_like: 'border-amber-500/20 bg-amber-500/5', premium: 'border-emerald-500/20 bg-emerald-500/5' };

  // Plan display metadata
  const PLAN_TIERS = {
    STARTER: { name: 'Starter', icon: Zap, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', ring: 'ring-blue-500/30' },
    PRO: { name: 'Pro', icon: Crown, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', ring: 'ring-amber-500/30', popular: true },
    ELITE: { name: 'Elite', icon: Star, color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', ring: 'ring-emerald-500/30' },
  };

  const monthlyPlans = plans.filter(p => p.interval?.includes('month'));

  return (
    <div className="min-h-screen bg-[#0a0a14] text-foreground" data-testid="onboarding-page">
      {/* Progress */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground font-medium">Step {screen} of {TOTAL_SCREENS}</span>
            <span className="text-xs text-emerald-400 font-medium">{Math.round((screen / TOTAL_SCREENS) * 100)}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-700" style={{ width: `${(screen / TOTAL_SCREENS) * 100}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2.5">
            {[
              { n: 1, label: 'Account', icon: Shield },
              { n: 2, label: 'AI Personality', icon: Mic },
              { n: 3, label: 'Schedule', icon: CalendarIcon },
              { n: 4, label: 'Plan', icon: CreditCard },
              { n: 5, label: 'Activate', icon: Rocket },
            ].map(s => (
              <div key={s.n} className={`flex items-center gap-1.5 text-[10px] transition-colors ${screen >= s.n ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                {screen > s.n ? <Check size={10} className="text-emerald-400" /> : <s.icon size={10} />}
                <span className="hidden sm:inline">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-28 pb-12 px-4 max-w-2xl mx-auto">

        {/* ═══ SCREEN 1: Welcome + Phone + Number ═══ */}
        {screen === 1 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="screen-1">
            {/* Welcome */}
            <div className="text-center mb-2">
              <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Rocket size={28} className="text-emerald-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Welcome to {brandName}!</h1>
              <p className="text-sm text-muted-foreground mt-1">Your AI will answer calls, qualify leads & book showings — let's set it up in 2 minutes.</p>
            </div>

            {/* Account confirmed */}
            <Card className="border-border bg-emerald-500/5 border-emerald-500/10">
              <CardContent className="p-3 flex items-center gap-3">
                <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
                <div>
                  <p className="text-sm text-foreground font-medium">Account created</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </CardContent>
            </Card>

            {/* Industry AI Preview */}
            {industryProfile && (
              <IndustryPreviewCard industry={industryProfile} compact={true} />
            )}

            {/* Company Name — required for AI greeting */}
            {!companyNameSaved ? (
              <Card className="border-border bg-card/80">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <UserPlus size={16} className="text-amber-400" />
                    <h2 className="text-sm font-semibold text-foreground">Company Name <span className="text-red-400">*</span></h2>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">Your AI will announce this when answering calls.</p>
                  <div className="space-y-3">
                    <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="ABC Real Estate Inc."
                      className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none"
                      data-testid="company-name-input" />
                    <Button onClick={saveCompanyName} disabled={loading || !companyName.trim()} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 text-sm" data-testid="save-company-btn">
                      {loading ? <Loader2 size={14} className="animate-spin" /> : 'Save & Continue'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-emerald-500/10 bg-emerald-500/5">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <UserPlus size={16} className="text-emerald-400" />
                    <div>
                      <p className="text-sm text-foreground font-medium">{companyName}</p>
                      <p className="text-[10px] text-muted-foreground">Company name saved</p>
                    </div>
                  </div>
                  <CheckCircle2 size={16} className="text-emerald-400" />
                </CardContent>
              </Card>
            )}

            {/* Phone Verification — only show after company name is saved */}
            {companyNameSaved && !phoneVerified ? (
              <Card className="border-border bg-card/80">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield size={16} className="text-blue-400" />
                    <h2 className="text-sm font-semibold text-foreground">Verify your phone</h2>
                  </div>
                  {!otpSent ? (
                    <div className="space-y-3">
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 123-4567"
                        className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-sm text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none"
                        data-testid="phone-input" />
                      <Button onClick={sendOtp} disabled={loading || !phone.trim()} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 text-sm" data-testid="send-otp-btn">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : <><MessageSquare size={14} className="mr-2" /> Send code</>}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {previewCode && (
                        <div className="p-2.5 bg-amber-500/5 border border-amber-500/10 rounded-lg flex items-center gap-2">
                          <Clock size={12} className="text-amber-400 shrink-0" />
                          <p className="text-xs text-amber-400">Preview code: <strong className="text-foreground font-mono">{previewCode}</strong></p>
                        </div>
                      )}
                      <input type="text" value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} placeholder="123456"
                        className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-center text-xl font-mono tracking-[0.4em] text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none"
                        data-testid="otp-input" />
                      <Button onClick={verifyOtp} disabled={loading || otp.length < 6} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-11 text-sm" data-testid="verify-otp-btn">
                        {loading ? <Loader2 size={14} className="animate-spin" /> : 'Verify'}
                      </Button>
                      <button onClick={() => { setOtpSent(false); setOtp(''); setPreviewCode(''); }} className="w-full text-xs text-muted-foreground hover:text-foreground">Resend code</button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : companyNameSaved && !selectedNumber ? (
              /* Pick Number */
              <Card className="border-border bg-card/80">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Hash size={16} className="text-purple-400" />
                    <h2 className="text-sm font-semibold text-foreground">Choose your AI phone number</h2>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <input type="text" value={areaCode} onChange={e => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                      onKeyDown={e => { if (e.key === 'Enter' && areaCode.length === 3) fetchNumbers(areaCode); }}
                      placeholder="Area code" maxLength={3}
                      className="flex-1 bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground font-mono placeholder-slate-500 focus:outline-none focus:border-emerald-500/50"
                      data-testid="area-code-input" />
                    <Button onClick={() => fetchNumbers(areaCode)} disabled={areaCode.length !== 3 || searchingNumbers}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white h-[42px] px-4 text-sm shrink-0" data-testid="area-code-search-btn">
                      {searchingNumbers ? <Loader2 size={14} className="animate-spin" /> : 'Search'}
                    </Button>
                  </div>
                  {!searchingNumbers && numbersMessage && (
                    <p className="text-xs text-amber-400/90 mb-2" data-testid="number-search-message">{numbersMessage}</p>
                  )}
                  {searchingNumbers ? (
                    <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground"><Loader2 size={14} className="animate-spin" /> Finding numbers...</div>
                  ) : numbers.length > 0 ? (
                    <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1" data-testid="number-list">
                      {numbers.map((n, i) => (
                        <button key={n.raw} onClick={() => pickNumber(n)} disabled={loading}
                          className="w-full flex items-center justify-between p-2.5 bg-muted hover:bg-muted border border-border hover:border-emerald-500/20 rounded-lg transition-all group"
                          data-testid={`number-option-${i}`}>
                          <div className="flex items-center gap-2.5">
                            <Phone size={13} className="text-muted-foreground group-hover:text-emerald-400" />
                            <span className="text-sm font-mono text-foreground">{n.number}</span>
                            <span className="text-[10px] text-muted-foreground">{n.area}</span>
                          </div>
                          <ChevronRight size={12} className="text-muted-foreground group-hover:text-emerald-400" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-4">Enter an area code to find available numbers</p>
                  )}
                  {loading && <div className="flex items-center justify-center py-3 gap-2 text-emerald-400"><Loader2 size={14} className="animate-spin" /> Assigning...</div>}
                </CardContent>
              </Card>
            ) : (
              /* Number confirmed */
              <Card className="border-emerald-500/10 bg-emerald-500/5">
                <CardContent className="p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <Phone size={16} className="text-emerald-400" />
                    <div>
                      <p className="text-sm text-foreground font-medium font-mono">{selectedNumber?.display || selectedNumber?.raw || 'Assigned'}</p>
                      <p className="text-[10px] text-muted-foreground">AI phone number assigned</p>
                    </div>
                  </div>
                  <CheckCircle2 size={16} className="text-emerald-400" />
                </CardContent>
              </Card>
            )}

            {/* Next button when number selected */}
            {selectedNumber && (
              <div className="space-y-2.5">
                <Button onClick={() => setScreen(2)} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-sm font-medium" data-testid="screen1-next-btn">
                  Customize my AI <ArrowRight size={16} className="ml-2" />
                </Button>
                <Button onClick={quickSetup} disabled={loading} variant="outline"
                  className="w-full border-border text-foreground hover:text-foreground hover:bg-muted h-11 text-sm font-medium gap-2"
                  data-testid="quick-setup-btn">
                  {loading ? <Loader2 size={14} className="animate-spin" /> : <><Zap size={14} className="text-amber-400" /> Quick Setup — use defaults &amp; activate</>}
                </Button>
                <p className="text-center text-[10px] text-muted-foreground">Quick Setup uses Standard voice, English, M-F 9-5. You can change later.</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ SCREEN 2: AI Personality (Voice + Language + Greeting) ═══ */}
        {screen === 2 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="screen-2">
            <div className="text-center mb-2">
              <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-4">
                <Mic size={28} className="text-amber-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Set up your AI personality</h1>
              <p className="text-sm text-muted-foreground mt-1">Choose how your AI sounds and greets callers.</p>
            </div>

            {/* Voice Selection */}
            <Card className="border-border bg-card/80">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Volume2 size={14} className="text-blue-400" />
                  <h3 className="text-sm font-semibold text-foreground">Voice</h3>
                </div>
                <div className="space-y-2" data-testid="voice-options">
                  {(voiceOptions.length > 0 ? voiceOptions : [
                    { tier: 'standard', label: 'Standard Voice', description: 'Clear and professional' },
                    { tier: 'human_like', label: 'Human-like Voice', description: 'Natural intonation' },
                    { tier: 'premium', label: 'Premium Voice', description: 'Ultra-realistic ElevenLabs' },
                  ]).map(v => (
                    <button key={v.tier} onClick={() => setSelectedVoice(v.tier)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${
                        selectedVoice === v.tier ? `${voiceColors[v.tier]} ring-1 ring-emerald-500/30` : 'border-border bg-muted/20 hover:bg-muted'
                      }`} data-testid={`voice-${v.tier}`}>
                      <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        {voiceIcons[v.tier]}
                      </div>
                      <div className="text-left flex-1">
                        <p className="text-sm font-medium text-foreground">{v.label}</p>
                        <p className="text-[10px] text-muted-foreground">{v.description}</p>
                      </div>
                      {selectedVoice === v.tier && <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Language */}
            <Card className="border-border bg-card/80">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Globe size={14} className="text-blue-400" />
                  <h3 className="text-sm font-semibold text-foreground">Language</h3>
                </div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {LANG_OPTIONS.slice(0, 3).map(l => (
                    <button key={l.code} onClick={() => setDefaultLang(l.code)}
                      className={`py-2 rounded-lg text-xs font-medium transition-all ${
                        defaultLang === l.code ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-muted text-muted-foreground border border-border'
                      }`} data-testid={`lang-${l.code}`}>{l.label}</button>
                  ))}
                </div>
                <label className="flex items-center justify-between cursor-pointer p-2.5 bg-muted/20 border border-border/30 rounded-lg">
                  <div>
                    <p className="text-xs text-foreground font-medium">Multilingual</p>
                    <p className="text-[10px] text-muted-foreground">Auto-detect & respond in caller's language</p>
                  </div>
                  <div className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${multilingualEnabled ? 'bg-emerald-500' : 'bg-accent'}`}
                    onClick={() => setMultilingualEnabled(!multilingualEnabled)} data-testid="multilingual-toggle">
                    <div className={`absolute top-0.5 left-0.5 h-4 w-4 bg-card rounded-full transition-transform ${multilingualEnabled ? 'translate-x-5' : ''}`} />
                  </div>
                </label>
                {multilingualEnabled && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {LANG_OPTIONS.filter(l => l.code !== defaultLang).map(l => (
                      <button key={l.code} onClick={() => setExtraLangs(p => p.includes(l.code) ? p.filter(x => x !== l.code) : [...p, l.code])}
                        className={`px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                          extraLangs.includes(l.code) ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-muted text-muted-foreground border border-border'
                        }`} data-testid={`extra-lang-${l.code}`}>{l.label}</button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Greeting */}
            <Card className="border-border bg-card/80">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Type size={14} className="text-violet-400" />
                  <h3 className="text-sm font-semibold text-foreground">Greeting</h3>
                </div>
                {greetingTemplates.length > 0 && (
                  <div className="flex gap-1.5 mb-3">
                    {greetingTemplates.map(t => (
                      <button key={t.type} onClick={() => { setSelectedTemplate(t.type); setGreetingText(t.text); }}
                        className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                          selectedTemplate === t.type ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' : 'bg-muted text-muted-foreground border border-border'
                        }`} data-testid={`template-${t.type}`}>{t.label}</button>
                    ))}
                  </div>
                )}
                <textarea value={greetingText} onChange={e => setGreetingText(e.target.value)} rows={3}
                  placeholder="Hello! I'm your AI assistant..."
                  className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none resize-none"
                  data-testid="greeting-textarea" />
              </CardContent>
            </Card>

            <Button onClick={savePersonality} disabled={loading || !greetingText.trim()} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-sm font-medium" data-testid="screen2-next-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <>Continue <ArrowRight size={16} className="ml-2" /></>}
            </Button>
            <Button onClick={quickSetup} disabled={loading} variant="outline"
              className="w-full border-border text-foreground hover:text-foreground hover:bg-muted h-10 text-xs font-medium gap-2"
              data-testid="quick-setup-btn-screen2">
              <Zap size={13} className="text-amber-400" /> Skip — use defaults &amp; activate
            </Button>
          </div>
        )}

        {/* ═══ SCREEN 3: Calendar + First Lead ═══ */}
        {screen === 3 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="screen-3">
            <div className="text-center mb-2">
              <div className="h-14 w-14 rounded-2xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center mx-auto mb-4">
                <CalendarIcon size={28} className="text-teal-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Schedule & first lead</h1>
              <p className="text-sm text-muted-foreground mt-1">Set when you're free for showings and add a contact.</p>
            </div>

            {/* Calendar */}
            <Card className="border-border bg-card/80">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <CalendarIcon size={14} className="text-teal-400" />
                  <h3 className="text-sm font-semibold text-foreground">Availability</h3>
                </div>
                <div className="space-y-2" data-testid="calendar-setup">
                  {availability.map((slot, i) => (
                    <div key={slot.day} className="flex items-center gap-2" data-testid={`avail-${slot.day}`}>
                      <button onClick={() => {
                        const a = [...availability]; a[i].enabled = !a[i].enabled; setAvailability(a);
                      }} className={`w-16 py-1.5 rounded-lg text-[10px] font-medium text-center transition-all ${
                        slot.enabled ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-muted text-muted-foreground border border-border'
                      }`}>{slot.day.slice(0, 3).charAt(0).toUpperCase() + slot.day.slice(1, 3)}</button>
                      {slot.enabled ? (
                        <div className="flex items-center gap-1.5 flex-1">
                          <input type="time" value={slot.start} onChange={e => {
                            const a = [...availability]; a[i].start = e.target.value; setAvailability(a);
                          }} className="bg-muted border border-border rounded-lg px-2 py-1 text-[11px] text-foreground focus:outline-none focus:border-emerald-500/50" />
                          <span className="text-[10px] text-muted-foreground">—</span>
                          <input type="time" value={slot.end} onChange={e => {
                            const a = [...availability]; a[i].end = e.target.value; setAvailability(a);
                          }} className="bg-muted border border-border rounded-lg px-2 py-1 text-[11px] text-foreground focus:outline-none focus:border-emerald-500/50" />
                        </div>
                      ) : <span className="text-[10px] text-muted-foreground flex-1">Off</span>}
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-[10px] text-muted-foreground mb-1.5">Showing duration</p>
                  <div className="flex gap-1.5">
                    {[15, 30, 45, 60].map(d => (
                      <button key={d} onClick={() => setShowingDuration(d)}
                        className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
                          showingDuration === d ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' : 'bg-muted text-muted-foreground border border-border'
                        }`} data-testid={`duration-${d}`}>{d}m</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* First Lead */}
            <Card className="border-border bg-card/80">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <UserPlus size={14} className="text-blue-400" />
                    <h3 className="text-sm font-semibold text-foreground">First lead</h3>
                  </div>
                  <Badge className="text-[9px] bg-muted text-muted-foreground border-border">Optional</Badge>
                </div>
                <div className="space-y-2.5">
                  <input type="text" value={leadName} onChange={e => setLeadName(e.target.value)} placeholder="Lead's name"
                    className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none"
                    data-testid="lead-name-input" />
                  <input type="tel" value={leadPhone} onChange={e => setLeadPhone(e.target.value)} placeholder="Phone number"
                    className="w-full bg-muted border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder-slate-500 focus:border-emerald-500/50 focus:outline-none"
                    data-testid="lead-phone-input" />
                  <div className="flex gap-1.5">
                    {['buyer', 'seller', 'investor'].map(t => (
                      <button key={t} onClick={() => setLeadType(t)}
                        className={`flex-1 py-2 rounded-lg text-[10px] font-medium transition-all ${
                          leadType === t ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-muted text-muted-foreground border border-border'
                        }`} data-testid={`lead-type-${t}`}>{t.charAt(0).toUpperCase() + t.slice(1)}</button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button onClick={saveCalendarAndLead} disabled={loading} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-sm font-medium" data-testid="screen3-next-btn">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <>Continue <ArrowRight size={16} className="ml-2" /></>}
            </Button>
            <Button onClick={quickSetup} disabled={loading} variant="outline"
              className="w-full border-border text-foreground hover:text-foreground hover:bg-muted h-10 text-xs font-medium gap-2"
              data-testid="quick-setup-btn-screen3">
              <Zap size={13} className="text-amber-400" /> Skip — use defaults &amp; activate
            </Button>
          </div>
        )}

        {/* ═══ SCREEN 4: Choose Plan ═══ */}
        {screen === 4 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="screen-4">
            <div className="text-center mb-2">
              <div className="h-14 w-14 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-4">
                <CreditCard size={28} className="text-violet-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Choose your plan</h1>
              <p className="text-sm text-muted-foreground mt-1">Start with a free trial or go pro right away.</p>
            </div>

            {/* Confirming payment return */}
            {confirmingPayment && (
              <Card className="border-emerald-500/20 bg-emerald-500/5">
                <CardContent className="p-5 flex items-center justify-center gap-3">
                  <Loader2 size={18} className="animate-spin text-emerald-400" />
                  <p className="text-sm text-emerald-300">Confirming your payment...</p>
                </CardContent>
              </Card>
            )}

            {/* Payment confirmed */}
            {paymentConfirmed && !confirmingPayment && selectedPlan === 'paid' && (
              <Card className="border-emerald-500/20 bg-emerald-500/5">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-300">Payment confirmed!</p>
                    <p className="text-xs text-muted-foreground">Your card has been saved securely.</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {paymentError && (
              <Card className="border-red-500/20 bg-red-500/5">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertCircle size={18} className="text-red-400 shrink-0" />
                  <p className="text-sm text-red-300">{paymentError}</p>
                </CardContent>
              </Card>
            )}

            {!confirmingPayment && !paymentConfirmed && (
              <>
                {/* Trial Option */}
                <button
                  onClick={() => { setSelectedPlan('trial'); setSelectedPlanCode(null); }}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedPlan === 'trial'
                      ? 'border-emerald-500/30 bg-emerald-500/5 ring-1 ring-emerald-500/20'
                      : 'border-border bg-card/80 hover:border-border'
                  }`}
                  data-testid="plan-trial"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <Zap size={18} className="text-emerald-400" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">Free Trial</p>
                        <p className="text-xs text-muted-foreground">7 days &middot; 5 min AI calls &middot; 50 SMS</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-emerald-400">$0</p>
                      <p className="text-[10px] text-muted-foreground">No card required</p>
                    </div>
                  </div>
                </button>

                {/* Divider */}
                <div className="flex items-center gap-3 px-2">
                  <div className="flex-1 h-px bg-muted" />
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">or choose a paid plan</span>
                  <div className="flex-1 h-px bg-muted" />
                </div>

                {/* Paid Plans */}
                <div className="space-y-3">
                  {monthlyPlans.map(plan => {
                    const tierKey = plan.plan_code.includes('STARTER') ? 'STARTER' : plan.plan_code.includes('PRO') ? 'PRO' : 'ELITE';
                    const tier = PLAN_TIERS[tierKey];
                    const TierIcon = tier.icon;
                    const isSelected = selectedPlan === 'paid' && selectedPlanCode === plan.plan_code;

                    return (
                      <button
                        key={plan.plan_code}
                        onClick={() => { setSelectedPlan('paid'); setSelectedPlanCode(plan.plan_code); }}
                        className={`w-full text-left p-4 rounded-xl border transition-all relative ${
                          isSelected
                            ? `${tier.border} ${tier.bg} ring-1 ${tier.ring}`
                            : 'border-border bg-card/80 hover:border-border'
                        }`}
                        data-testid={`plan-${plan.plan_code}`}
                      >
                        {tier.popular && (
                          <Badge className="absolute -top-2 right-3 bg-amber-500/90 text-foreground text-[9px] px-2 py-0.5 border-0">
                            Most Popular
                          </Badge>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-lg ${tier.bg} flex items-center justify-center`}>
                              <TierIcon size={18} className={tier.color} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">{tier.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {plan.limits?.sms_per_month || 500} SMS &middot; {plan.limits?.voice_minutes_per_month || 150} min calls
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-lg font-bold ${tier.color}`}>{plan.amount_display}</p>
                            <p className="text-[10px] text-muted-foreground">per month</p>
                          </div>
                        </div>
                        {isSelected && <CheckCircle2 size={16} className={`absolute top-4 right-4 ${tier.color}`} />}
                      </button>
                    );
                  })}
                </div>

                {/* Action */}
                <Button
                  onClick={selectPlan}
                  disabled={loading}
                  className={`w-full h-12 text-sm font-medium ${
                    selectedPlan === 'trial'
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                      : 'bg-gradient-to-r from-violet-500 to-violet-600 hover:from-violet-600 hover:to-violet-700 text-foreground'
                  }`}
                  data-testid="plan-continue-btn"
                >
                  {loading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : selectedPlan === 'trial' ? (
                    <>Start Free Trial <ArrowRight size={16} className="ml-2" /></>
                  ) : (
                    <>Continue to Payment <ExternalLink size={14} className="ml-2" /></>
                  )}
                </Button>

                {selectedPlan === 'paid' && (
                  <p className="text-center text-[10px] text-muted-foreground">
                    You'll be redirected to our secure payment page to enter your card.
                  </p>
                )}
              </>
            )}

            {/* After payment confirmed, advance to activate */}
            {paymentConfirmed && !confirmingPayment && (
              <Button
                onClick={() => setScreen(5)}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white h-12 text-sm font-medium"
                data-testid="plan-next-btn"
              >
                Continue to Activation <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        )}

        {/* ═══ SCREEN 5: Confirm & Activate ═══ */}
        {screen === 5 && (
          <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="screen-5">
            <div className="text-center mb-2">
              <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <Rocket size={28} className="text-emerald-400" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-foreground">Ready to go live!</h1>
              <p className="text-sm text-muted-foreground mt-1">Review your setup and activate your AI assistant.</p>
            </div>

            <Card className="border-border bg-card/80">
              <CardContent className="p-5 space-y-2" data-testid="setup-summary">
                <SummaryRow icon={<Phone size={13} />} label="Phone Number" value={selectedNumber?.display || selectedNumber?.raw || 'Assigned'} />
                <SummaryRow icon={<Mic size={13} />} label="Voice" value={selectedVoice === 'human_like' ? 'Human-like' : selectedVoice.charAt(0).toUpperCase() + selectedVoice.slice(1)} />
                <SummaryRow icon={<Globe size={13} />} label="Language" value={`${LANG_OPTIONS.find(l => l.code === defaultLang)?.label || 'English'}${multilingualEnabled ? ' + Multilingual' : ''}`} />
                <SummaryRow icon={<Type size={13} />} label="Greeting" value={greetingText ? `"${greetingText.slice(0, 40)}..."` : 'Custom'} />
                <SummaryRow icon={<CalendarIcon size={13} />} label="Availability" value={`${availability.filter(a => a.enabled).length} days, ${showingDuration}min showings`} />
                <SummaryRow icon={<CreditCard size={13} />} label="Plan" value={
                  selectedPlan === 'trial' ? 'Free Trial (7 days)' :
                  plans.find(p => p.plan_code === selectedPlanCode)?.plan_name || selectedPlanCode || 'Paid'
                } />
                {leadName && <SummaryRow icon={<UserPlus size={13} />} label="First Lead" value={leadName} />}
              </CardContent>
            </Card>

            <Button onClick={activate} disabled={loading}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-foreground h-14 text-base font-semibold shadow-lg shadow-emerald-500/20"
              data-testid="activate-ai-btn">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <><Rocket size={18} className="mr-2" /> Activate My AI</>}
            </Button>

            <p className="text-center text-[10px] text-muted-foreground">You can change any of these settings later.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 px-3 bg-muted/20 rounded-lg" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 text-muted-foreground">{icon}<span className="text-xs">{label}</span></div>
      <span className="text-xs font-medium text-foreground truncate max-w-[180px]">{value}</span>
    </div>
  );
}
